import AppForm from '@/components/AppForm';
import { ErrorResponseModal, SuccessResponseModal } from '@/components/AppModal/modals';
import AppPageTitle from '@/components/AppPageTitle';
import PATH from '@/config/paths/PATH';
import { useUnsaved } from '@/contexts/UnsavedContext';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getThemeDefaultItems, postThemeManagement, postThemeManagementFormData } from '@/services/theme-management';
import { useMutation } from '@tanstack/react-query';
import { Form } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormItem from './components/FormItem';

const START_DATE = dayjs().startOf('month').format('YYYY-MM-DD');
const END_DATE = dayjs().endOf('month').format('YYYY-MM-DD');

export default function CreateTheme() {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { setHasUnsaved } = useUnsaved();
	// các item (kèm ảnh) đang chờ, chỉ thực sự submit khi bấm nút submit chính của form
	const [pendingItems, setPendingItems] = useState({});

	const { data } = usePaginatedQuery(
		() =>
			getThemeDefaultItems(),
		['themes-default-item']
	);

	const createThemeMutation = useMutation({
		mutationFn: (values) => {
			const payload = {
				themeName: values?.themeName || '',
				description: values?.description || "",
				startTime: values?.startTime,
				endTime: values?.endTime
			}

			return postThemeManagement(payload);
		},
	});

	// mutation riêng cho việc submit item (kèm ảnh) dạng multipart/form-data
	const submitItemMutation = useMutation({
		mutationFn: (formData) => postThemeManagementFormData(formData),
	});

	// chọn ảnh (có thể chọn nhiều ảnh) cho 1 item, chỉ lưu tạm, chưa gọi API
	const handleSubmitItem = async (payload, closePopup) => {
		const imageFieldKey = payload?.itemCode;
		if (!imageFieldKey) {
			ErrorResponseModal({
				message: 'Unable to determine the item type (itemCode)',
			});
			return;
		}

		const images = payload?.newImages || (payload?.newImage ? [payload.newImage] : []);
		if (!images.length || images.some((file) => !(file instanceof File))) {
			ErrorResponseModal({
				message: 'Please select a photo',
			});
			return;
		}

		setPendingItems((prev) => ({
			...prev,
			[imageFieldKey]: { id: payload?.id, itemCode: imageFieldKey, images },
		}));
		setHasUnsaved(true);
		closePopup?.();
	};

	// không có ảnh -> gọi JSON; có ảnh -> gộp toàn bộ theme + item vào 1 form-data duy nhất
	const handleOnSubmit = async (values) => {
		const items = Object.values(pendingItems);

		try {
			if (items.length === 0) {
				await createThemeMutation.mutateAsync(values);
			} else {
				const formData = new FormData();
				formData.append('themeName', values?.themeName || '');
				formData.append('startTime', values?.startTime || '');
				formData.append('endTime', values?.endTime || '');
				formData.append('description', values?.description || '');
				items.forEach((pendingItem) => {
					// nhiều ảnh cùng itemCode -> append nhiều lần, không ghi đè
					pendingItem.images.forEach((file) => formData.append(pendingItem.itemCode, file));
				});

				await submitItemMutation.mutateAsync(formData);
			}

			setPendingItems({});
			setHasUnsaved(false);
			SuccessResponseModal({
				onOk: () => {
					navigate(PATH.THEME_MANAGEMENT.ROOT);
				},
			});
		} catch (error) {
			const errorMessage =
				error?.data?.errorMessage || error?.response?.data?.errorMessage || 'Something went wrong';
			ErrorResponseModal({
				message: errorMessage,
			});
		}
	};

	return (
		<div>
			<AppPageTitle title="Create Theme" />
			<AppForm
				form={form}
				onSubmit={handleOnSubmit}
				targetPath={PATH.THEME_MANAGEMENT.ROOT}
				buttonProps={{
					loading: createThemeMutation.isPending || submitItemMutation.isPending,
					disabled: createThemeMutation.isPending || submitItemMutation.isPending,
					// chọn ảnh xong dù chưa "touch" field nào của form chính vẫn phải cho phép bấm Save
					saveDisabled:
						(createThemeMutation.isPending || submitItemMutation.isPending) ||
						(!form.isFieldsTouched() && Object.keys(pendingItems).length === 0),
				}}
				disabled={createThemeMutation.isPending}
			>
				<FormItem
					form={form}
					END_DATE={END_DATE}
					START_DATE={START_DATE}
					dataDefault={data?.data}
					handleSubmitItem={handleSubmitItem}
					pendingItems={pendingItems}
					isCreate={false}
				/>
			</AppForm>
		</div>
	)
}
